import React, { Fragment, useState } from "react";
import {
  CHAN_SHORT,
  cleanReviewText,
  fmtDay,
  fmtInt,
  isPaid,
  str,
  toneOf,
  truthy,
  type PostRow,
  type Tone,
  type ToneCount,
} from "./resumenData";

const PAGE = 15;
const COLUMNAS = 7;
const TONE_LABEL: Record<Tone, string> = { pos: "Positiva", neg: "Negativa", neu: "Neutra", none: "Sin clasificar" };
const TONE_TAG: Record<Tone, string> = { pos: "good", neg: "bad", neu: "stable", none: "stable" };

function ToneBar({ tone }: { tone: ToneCount }) {
  const classified = tone.pos + tone.neu + tone.neg;
  const w = (n: number) => `${classified > 0 ? ((n / classified) * 100).toFixed(1) : 0}%`;
  return (
    <div
      className="sbx-tonebar"
      style={{ height: 12 }}
      role="img"
      aria-label={`${tone.pos} positivos, ${tone.neu} neutros, ${tone.neg} negativos`}
    >
      <i className="pos" style={{ width: w(tone.pos) }} />
      <i className="neu" style={{ width: w(tone.neu) }} />
      <i className="neg" style={{ width: w(tone.neg) }} />
    </div>
  );
}

interface Props {
  posts: PostRow[];
  /** Abre la Bandeja en ese comentario. */
  onOpenComment?: (feedbackId: string) => void;
}

/** Publicaciones con más conversación: cada fila se despliega con sus comentarios. Una fila abierta a la vez. */
export function PublicacionesConversacion({ posts, onOpenComment }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE);

  const toggle = (id: string) => {
    setShown(PAGE);
    setOpenId((cur) => (cur === id ? null : id));
  };

  return (
    <div className="sbx-table-wrap">
      <table className="sbx-table sbx-posts">
        <thead>
          <tr>
            <th>Publicación o anuncio</th>
            <th>Canal</th>
            <th>Origen</th>
            <th className="num">Comentarios</th>
            <th style={{ width: "18%" }}>Tono</th>
            <th>Lo que más se repite</th>
            <th className="num">Sin responder</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((p) => {
            const open = openId === p.id;
            return (
              <Fragment key={p.id}>
                <tr className={`sbx-post-row${open ? " is-open" : ""}`} data-testid="post-fila" onClick={() => toggle(p.id)}>
                  <td className="name">
                    <button type="button" className="sbx-post-toggle" aria-expanded={open}>
                      <span className="chev" aria-hidden="true">
                        ›
                      </span>
                      {p.name}
                    </button>
                  </td>
                  <td>{CHAN_SHORT[p.chan]}</td>
                  <td>
                    <span className={`sbx-origin${p.paid ? " paid" : ""}`}>{p.paid ? "Anuncio" : "Orgánico"}</span>
                  </td>
                  <td className="num">{fmtInt(p.total)}</td>
                  <td>
                    <ToneBar tone={p.tone} />
                  </td>
                  <td className="theme">{p.repeated}</td>
                  <td className="num">
                    <strong className={p.pending > 0 ? "is-bad" : undefined}>{fmtInt(p.pending)}</strong>
                  </td>
                </tr>
                {open && (
                  <tr className="sbx-post-panel">
                    <td colSpan={COLUMNAS}>
                      <div data-testid="post-comentarios">
                        <table className="sbx-post-comments">
                          <thead>
                            <tr>
                              <th>Autor</th>
                              <th>Fecha</th>
                              <th>Comentario</th>
                              <th>Tono</th>
                              <th>Estado</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {p.comments.slice(0, shown).map((c, i) => {
                              const tone = toneOf(c);
                              const answered = truthy(c.has_reply);
                              const id = str(c.feedback_id);
                              return (
                                <tr
                                  key={id || `${str(c.author_name)}|${str(c.date)}|${i}`}
                                  data-testid="post-comentario"
                                  data-origen={isPaid(c) ? "paid" : "organic"}
                                >
                                  <td className="author">{str(c.author_name).trim() || "Cliente"}</td>
                                  <td className="date">{fmtDay(str(c.date))}</td>
                                  <td className="msg">{cleanReviewText(c.message) || "Sin texto"}</td>
                                  <td>
                                    <span className={`sbx-loc-tag ${TONE_TAG[tone]}`}>{TONE_LABEL[tone]}</span>
                                  </td>
                                  <td>
                                    <span className="sbx-loc-tag stable">{answered ? "Respondida" : "Sin responder"}</span>
                                  </td>
                                  <td className="go">
                                    {!answered && id && onOpenComment && (
                                      <button type="button" className="sbx-link" onClick={() => onOpenComment(id)}>
                                        Abrir en la Bandeja
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {p.comments.length > shown && (
                          <button
                            type="button"
                            className="sbx-btn-secondary"
                            data-testid="post-ver-mas"
                            onClick={() => setShown((n) => n + PAGE)}
                          >
                            Ver {Math.min(PAGE, p.comments.length - shown)} más
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
